"use client";

import { HeartIcon as HeartIconOutline } from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import { cloneDeep } from "lodash";
import { useState } from "react";
import { Tooltip } from "react-tooltip";

import { changeSavedArtistInstrumentAction } from "@/data/artistInstrument/actions/changeSavedArtistInstrumentAction";
import { changeSavedMixedModelAction } from "@/data/mixedModel/actions/changeSavedMixedModelAction";
import { changeSavedModelAction } from "@/data/model/actions/changeSavedModelAction";
import {
  Artist,
  ArtistInstumentSaved,
  Instrument,
  MixedModelConstructedNoDates,
  ModelNoDates,
  UserNoDates,
} from "@/types/types";
import { isArtistTypeGuard } from "@/utils/artistInstrumentModelMixedModel/is/isArtistTypeGuard";
import { isInstrumentTypeGuard } from "@/utils/artistInstrumentModelMixedModel/is/isInstrumentTypeGuard";
import { isMixedModelTypeGuard } from "@/utils/artistInstrumentModelMixedModel/is/isMixedModelTypeGuard";
import { isModelTypeGuard } from "@/utils/artistInstrumentModelMixedModel/is/isModelTypeGuard";
import { isFreeUser } from "@/utils/subscriptionLevels/isFreeUser";

export function SaveButton({
  user,
  artistInstrumentModelMixedModel,
  savedArtistsInstrumentsIds,
}: {
  user: UserNoDates | null;
  artistInstrumentModelMixedModel:
    | Artist
    | Instrument
    | ModelNoDates
    | MixedModelConstructedNoDates;
  savedArtistsInstrumentsIds: ArtistInstumentSaved["artistInstrumentId"][];
}) {
  // Massage artistInstrumentModelMixedModel so the saved property is there
  let artistInstrumentModelMixedModelConstructed:
    | (Artist & { saved: boolean })
    | (Instrument & { saved: boolean })
    | ModelNoDates
    | MixedModelConstructedNoDates;
  if (
    isArtistTypeGuard(artistInstrumentModelMixedModel) ||
    isInstrumentTypeGuard(artistInstrumentModelMixedModel)
  ) {
    const saved = savedArtistsInstrumentsIds.includes(
      artistInstrumentModelMixedModel.id
    );

    artistInstrumentModelMixedModelConstructed = {
      ...cloneDeep(artistInstrumentModelMixedModel),
      saved: saved,
    };
  } else {
    artistInstrumentModelMixedModelConstructed = cloneDeep(
      artistInstrumentModelMixedModel
    );
  }

  // Allows for optimistic updates of the UI
  const [
    optimisticArtistInstrumentModelMixedModelConstructed,
    setOptimisticArtistInstrumentModelMixedModelConstructed,
  ] = useState(artistInstrumentModelMixedModelConstructed);

  // Free users can't save
  const canSave = user !== null && !isFreeUser(user.stripeSubscriptionId);

  if (!canSave) {
    return null;
  }

  // Save or unsave function
  const toggleSavedArtistsInstrumentsIds = async () => {
    const save = !optimisticArtistInstrumentModelMixedModelConstructed.saved;

    // Optimistically update saved
    setOptimisticArtistInstrumentModelMixedModelConstructed({
      ...cloneDeep(optimisticArtistInstrumentModelMixedModelConstructed),
      saved: save,
    });

    // Actually save or unsave the artist/instrument
    try {
      if (
        isArtistTypeGuard(artistInstrumentModelMixedModel) ||
        isInstrumentTypeGuard(artistInstrumentModelMixedModel)
      ) {
        await changeSavedArtistInstrumentAction({
          artistInstrumentId: artistInstrumentModelMixedModel.id,
          save: save,
        });
      } else if (isModelTypeGuard(artistInstrumentModelMixedModel)) {
        await changeSavedModelAction({
          modelId: artistInstrumentModelMixedModel.id,
          save: save,
        });
      } else if (isMixedModelTypeGuard(artistInstrumentModelMixedModel)) {
        await changeSavedMixedModelAction({
          mixedModelId: artistInstrumentModelMixedModel.id,
          save: save,
        });
      }
    } catch {
      console.error(
        `We couldn't ${
          save ? "favorite" : "unfavorite"
        } the voice. Please try again later.`
      );
    }
  };

  return (
    <div className="absolute right-4 top-2 z-40">
      <button onClick={toggleSavedArtistsInstrumentsIds}>
        {optimisticArtistInstrumentModelMixedModelConstructed.saved ?
          <HeartIconSolid
            data-tooltip-id={`save-voice-tooltip-${artistInstrumentModelMixedModel.id}`}
            data-tooltip-content="Unfavorite"
            data-tooltip-place="top"
            className="box-content h-6 w-6 fill-primary stroke-primary p-1 drop-shadow-strong"
          />
        : <HeartIconOutline
            data-tooltip-id={`save-voice-tooltip-${artistInstrumentModelMixedModel.id}`}
            data-tooltip-content="Favorite"
            data-tooltip-place="top"
            className="box-content h-6 w-6 stroke-white p-1 drop-shadow-strong"
          />
        }
      </button>
      <Tooltip
        id={`save-voice-tooltip-${artistInstrumentModelMixedModel.id}`}
      />
    </div>
  );
}
